import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Instagram, Facebook, Linkedin } from 'lucide-react';
import WhatsAppIcon from '@/components/WhatsAppIcon';
import { useTema, DEFAULT_LOGO } from '@/lib/TemaContext';
import { usePublicRoutes } from '@/lib/publicRoutes';
import { listRows } from '@/api/supabaseClient';
import { useCdiAge } from '@/hooks/useCdiAge';
import { optimizeImageUrl, restoreOriginalImage } from '@/lib/imageUrl';

function socialPath(value = '') {
  return value.trim().replace(/^@/, '').replace(/^\/+|\/+$/g, '').split('/').filter(Boolean).map(encodeURIComponent).join('/');
}

export default function Footer() {
  const cdiAge = useCdiAge();
  const { config } = useTema();
  const [organization, setOrganization] = useState({
    name: 'CDI Premium',
    legalName: 'CDI Premium',
    cnpj: 'XX.XXX.XXX/0001-XX',
    instagramUsername: '',
    facebookUsername: '',
    linkedinUsername: '',
  });
  const [terms, setTerms] = useState([]);
  const logoUrl = config?.logo_url || DEFAULT_LOGO;
  const optimizedLogoUrl = optimizeImageUrl(logoUrl, { width: 320, quality: 80, resize: 'contain' });
  const { path, label } = usePublicRoutes();
  const linkedInPath = socialPath(organization.linkedinUsername);
  const socialProfiles = [
    organization.instagramUsername && { label: 'Instagram', href: `https://www.instagram.com/${socialPath(organization.instagramUsername)}`, Icon: Instagram },
    organization.facebookUsername && { label: 'Facebook', href: `https://www.facebook.com/${socialPath(organization.facebookUsername)}`, Icon: Facebook },
    linkedInPath && { label: 'LinkedIn', href: `https://www.linkedin.com/${linkedInPath.includes('/') ? linkedInPath : `in/${linkedInPath}`}`, Icon: Linkedin },
  ].filter(Boolean);

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(() => {
      listRows('seo_config', { orderBy: null, limit: 1 }).then(([seoConfig]) => {
        if (!active || !seoConfig) return;
        setOrganization({
          name: seoConfig.organization_name || 'CDI Premium',
          legalName: seoConfig.organization_legal_name || seoConfig.organization_name || 'CDI Premium',
          cnpj: seoConfig.organization_cnpj || 'XX.XXX.XXX/0001-XX',
          instagramUsername: seoConfig.instagram_username || '',
          facebookUsername: seoConfig.facebook_username || '',
          linkedinUsername: seoConfig.linkedin_username || '',
        });
      }).catch(() => {
        // Mantém os valores seguros acima quando as configurações estão indisponíveis.
      });
    }, 2_500);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(() => {
      listRows('termos', { filters: { publicado: true }, orderBy: 'ordem', ascending: true, limit: 50 })
        .then(rows => {
          if (!active) return;
          setTerms(rows
            .filter(term => term.publicado && term.slug && term.titulo)
            .sort((left, right) => (left.ordem - right.ordem) || left.titulo.localeCompare(right.titulo, 'pt-BR')));
        })
        .catch(() => {
          if (active) setTerms([]);
        });
    }, 2_500);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, []);

  return (
    <footer className="bg-cdi-blue relative overflow-hidden">
      {/* Idade institucional decorativa */}
      <div
        className="absolute bottom-0 right-0 text-cdi-yellow font-black select-none pointer-events-none leading-none"
        style={{ fontSize: 'clamp(180px, 30vw, 320px)', opacity: 0.12, lineHeight: 0.8 }}
      >
        {cdiAge}
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16 md:py-20 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Logo + tagline */}
          <div className="md:col-span-1">
            <div className="mb-4 flex h-14 w-[252px] items-center">
              <img
                src={optimizedLogoUrl}
                alt={`CDI Premium — ${cdiAge} anos`}
                width="563"
                height="125"
                loading="lazy"
                decoding="async"
                onError={(event) => restoreOriginalImage(event, logoUrl)}
                className="h-full w-full object-contain object-left"
              />
            </div>
            <span className="block text-white/60 text-[9px] font-semibold tracking-widest uppercase leading-tight mb-4">Conexão • Dedicação • Inspiração</span>
            <p className="text-white/60 text-sm leading-relaxed mt-4 font-medium">
              Há {cdiAge} anos conectamos medicina e humanidade com excelência diagnóstica.
            </p>
            <div className="mt-6 inline-block bg-cdi-yellow px-4 py-2">
              <span className="text-cdi-blue font-black text-xs tracking-widest uppercase">{cdiAge} anos de excelência</span>
            </div>
          </div>

          {/* Contato */}
          <div>
            <h3 className="text-cdi-yellow font-black text-xs tracking-widest uppercase mb-5 pb-2 border-b-2 border-cdi-yellow">
              Contato
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-white/70 text-[10px] uppercase tracking-widest font-semibold mb-1">Telefone</p>
                <a href="tel:6231217272" className="text-white font-bold text-lg hover:text-cdi-yellow transition-colors flex items-center gap-2">
                  <Phone size={14} className="text-cdi-yellow" />
                  (62) 3121-7272
                </a>
              </div>
              <div>
                <p className="text-white/70 text-[10px] uppercase tracking-widest font-semibold mb-1">Agendamento</p>
                <Link
                  to={path('schedule')}
                  className="text-white font-bold text-lg hover:text-cdi-yellow transition-colors flex items-center gap-2"
                >
                  <WhatsAppIcon size={14} className="text-cdi-yellow" />
                  Agendar pelo WhatsApp
                </Link>
              </div>
              {socialProfiles.length > 0 && (
                <div>
                  <p className="text-white/70 text-[10px] uppercase tracking-widest font-semibold mb-2">Redes sociais</p>
                  <div className="flex flex-wrap gap-2">
                    {socialProfiles.map(({ label: socialLabel, href, Icon }) => (
                      <a key={socialLabel} href={href} target="_blank" rel="noopener noreferrer" aria-label={`CDI Premium no ${socialLabel}`} className="flex h-9 w-9 items-center justify-center border border-white/30 text-white transition-colors hover:border-cdi-yellow hover:bg-cdi-yellow hover:text-cdi-blue">
                        <Icon size={17} aria-hidden="true" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Endereço e horário */}
          <div>
            <h3 className="text-cdi-yellow font-black text-xs tracking-widest uppercase mb-5 pb-2 border-b-2 border-cdi-yellow">
              Localização
            </h3>
            <div className="space-y-4">
              <div className="flex gap-2">
                <MapPin size={16} className="text-cdi-yellow flex-shrink-0 mt-0.5" />
                <p className="text-white/80 text-sm leading-relaxed font-medium">
                  Goiânia — GO<br />
                  Novo prédio com tecnologia de ponta
                </p>
              </div>
              <div className="flex gap-2">
                <Clock size={16} className="text-cdi-yellow flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white/80 text-sm font-medium">Seg–Sex: 07h–18h</p>
                  <p className="text-white/80 text-sm font-medium">Sáb: 07h–12h</p>
                </div>
              </div>
            </div>
          </div>

          {/* Links rápidos */}
          <div>
            <h3 className="text-cdi-yellow font-black text-xs tracking-widest uppercase mb-5 pb-2 border-b-2 border-cdi-yellow">
              Links Rápidos
            </h3>
            <div className="space-y-3">
              {[
                'schedule', 'portal', 'exams', 'insurance', 'about', 'careers', 'contact', 'faq',
              ].map(pageKey => (
                <Link
                  key={pageKey}
                  to={path(pageKey)}
                  className="block text-white/70 text-sm font-medium hover:text-cdi-yellow hover:pl-2 transition-all border-l-2 border-transparent hover:border-cdi-yellow pl-0"
                >
                  {label(pageKey)}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/20 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/70 text-xs font-medium">
            © {new Date().getFullYear()} {organization.name}
          </p>
          <a
            href="https://www.figma.com/community/file/1166831539721848736"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/50 text-[10px] font-medium transition-colors hover:text-white"
          >
            Todos os direitos reservados. Razão social: {organization.legalName}. CNPJ: {organization.cnpj}
          </a>
          {terms.length > 0 && (
            <nav aria-label="Documentos legais" className="flex flex-wrap justify-center gap-x-6 gap-y-2">
              {terms.map(term => (
                <Link
                  key={term.id}
                  to={`/termos/${term.slug}`}
                  className="text-white/70 text-xs hover:text-white transition-colors font-medium"
                >
                  {term.titulo}
                </Link>
              ))}
            </nav>
          )}
        </div>
      </div>
    </footer>
  );
}
